import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAutoFocus } from '../utils/useAutoFocus';
import { capitalizeWords } from '../utils/formInputUtils';
import LanguageSwitch from '../components/LanguageSwitch';
import AppHeader from '../components/AppHeader';
import './Registration.css';
import './SsoSignupFlow.css';

export const SSO_SIGNUP_STEP1_KEY = 'mvp_sso_signup_step1';

/**
 * Step 1 of SSO signup funnel (after OAuth): names and applicant type - matches mockup `details-sso`.
 */
function readStoredUserNames() {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    return {
      first: u.first_name || u.firstName || '',
      last: u.last_name || u.lastName || '',
    };
  } catch {
    return { first: '', last: '' };
  }
}

export default function SsoSignupDetails() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(() => readStoredUserNames().first);
  const [lastName, setLastName] = useState(() => readStoredUserNames().last);
  const [applicantType, setApplicantType] = useState(null);
  const [hasTriedContinue, setHasTriedContinue] = useState(false);
  const mainRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const applicantTypeRef = useRef(null);

  useAutoFocus(firstNameRef, { select: false });

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const canContinue =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    applicantType !== null;

  const handleContinue = () => {
    if (!canContinue) return;
    try {
      sessionStorage.setItem(
        SSO_SIGNUP_STEP1_KEY,
        JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          applicantType,
        })
      );
    } catch {
      // ignore
    }
    navigate('/signup/sso-contact');
  };

  const focusAndScroll = (target) => {
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      target.focus({ preventScroll: true });
    }, 200);
  };

  const getFirstInvalidField = () => {
    if (!firstName.trim()) return firstNameRef.current;
    if (!lastName.trim()) return lastNameRef.current;
    if (!applicantType) return applicantTypeRef.current;
    return null;
  };

  const handleDisabledContinueAttempt = () => {
    if (canContinue) return;
    setHasTriedContinue(true);
    focusAndScroll(getFirstInvalidField());
  };

  const getEnterNavigationTargets = () => {
    const root = mainRef.current;
    if (!root) return [];
    const nodes = Array.from(root.querySelectorAll(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
    ));
    return nodes.filter((el) => {
      if (el.disabled) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      if (el.tabIndex < 0) return false;
      return true;
    });
  };

  const handleEnterAdvance = (e) => {
    if (e.key !== 'Enter') return;
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.target instanceof HTMLTextAreaElement) return;

    const targets = getEnterNavigationTargets();
    const idx = targets.indexOf(e.target);
    if (idx === -1) return;

    e.preventDefault();
    const next = targets[idx + 1];
    if (next) {
      next.focus();
      return;
    }

    // Last control: preserve existing continue action.
    handleContinue();
  };

  const choicesApplicant = [
    { value: 'me', label: t('ssoSignup.forMe'), icon: 'ri-user-3-line' },
    { value: 'someone_else', label: t('ssoSignup.forSomeoneElse'), icon: 'ri-user-shared-2-line' },
  ];

  return (
    <div className="reg-page">
      <AppHeader rightSlot={<LanguageSwitch />} />

      <main className="reg-main" ref={mainRef} onKeyDown={handleEnterAdvance}>
        <div className="reg-progress">
          <div className="sso-progress-seg sso-progress-seg--fill" />
          <div className="sso-progress-seg sso-progress-seg--fill" />
        </div>
        <div className="reg-step-row">
          <span>{t('ssoSignup.step2of2')}</span>
          <span className="reg-time">{t('ssoSignup.takesLessThanMinute')}</span>
        </div>

        <h1 className="sso-title">
          <Trans
            i18nKey="ssoSignup.detailsTitle"
            components={{ accent: <span className="sso-title-accent" /> }}
          />
        </h1>
        <p className="sso-subdesc">{t('ssoSignup.detailsSubtitle')}</p>

        <div className="reg-grid-2">
          <div>
            <label className="reg-label">{t('ssoSignup.firstName')}</label>
            <input
              ref={firstNameRef}
              type="text"
              className={`reg-input ${hasTriedContinue && !firstName.trim() ? 'reg-input--invalid' : ''}`}
              placeholder={t('ssoSignup.placeholderFirstName')}
              value={firstName}
              onChange={(e) => setFirstName(capitalizeWords(e.target.value))}
              autoFocus
              autoComplete="given-name"
            />
          </div>
          <div>
            <label className="reg-label">{t('ssoSignup.lastName')}</label>
            <input
              ref={lastNameRef}
              type="text"
              className={`reg-input ${hasTriedContinue && !lastName.trim() ? 'reg-input--invalid' : ''}`}
              placeholder={t('ssoSignup.placeholderLastName')}
              value={lastName}
              onChange={(e) => setLastName(capitalizeWords(e.target.value))}
              autoComplete="family-name"
            />
          </div>
        </div>

        <div className="reg-divider" style={{ margin: '16px 0 24px' }} />

        <div>
          <p className="reg-group-title">{t('ssoSignup.forQuestion')}</p>
          <div className="reg-choice-row">
            {choicesApplicant.map(({ value, label, icon }) => {
              const active = applicantType === value;
              return (
                <button
                  ref={value === 'me' ? applicantTypeRef : null}
                  key={value}
                  type="button"
                  className={`sso-choice-btn ${active ? 'sso-choice-btn--active' : ''}`}
                  onClick={() => setApplicantType(value)}
                >
                  <span className="sso-choice-icon">
                    <i className={icon} aria-hidden />
                  </span>
                  <span>{label}</span>
                  {active && (
                    <span className="sso-choice-check">
                      <i className="ri-check-line" aria-hidden />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div onClick={handleDisabledContinueAttempt}>
          <button
            type="button"
            className="btn-primary"
            style={{ marginTop: 20 }}
            disabled={!canContinue}
            onClick={handleContinue}
          >
            {t('ssoSignup.continue')}
          </button>
        </div>

        <p className="reg-footnote" style={{ marginTop: 16 }}>
          {t('ssoSignup.noDocsNeeded')}{' '}
          <span style={{ color: '#4b5563' }}>{t('ssoSignup.guideEverything')}</span>
        </p>
      </main>
    </div>
  );
}
